import { Link } from '@tanstack/react-router'

import Marker from '@/components/motion/Marker'
import { buttonVariants } from '@/components/ui/button-variants'
import Modal from '@/components/ui/Modal'
import {
  isMoodToday,
  MOOD_BANDS,
  moodBandByKey,
  type MoodBandKey,
} from '@/features/market/lib/mood'
import { formatBsDate } from '@/lib/bsDate'
import { cn } from '@/lib/utils'
import { moodArt } from '@/screens/app/home/components/mood/moodArt'
import type { MarketMood } from '@/schemas/market'

const FILL: Readonly<Record<MoodBandKey, string>> = {
  fear: 'bg-mood-fear',
  cautious: 'bg-mood-cautious',
  neutral: 'bg-mood-neutral',
  warm: 'bg-mood-warm',
  greed: 'bg-mood-greed',
}

interface MoodExplainerProps {
  mood: MarketMood
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * "Fear and greed · NEPSE" — what the mood cell opens (the mock's explainer): the latest reading
 * with Mitra in its pose, the five bands with a marker at the score, how the score is measured,
 * the five moods with the reading's lit, and the note that it is not a signal. "Open Market" goes
 * to Market · Overview.
 *
 * The score is mm-be's composite, a percentile against NEPSE's own recent sessions, so the scale
 * under the bands is read against those and not against a price range. The nine signals are
 * named in a sentence and not drawn.
 */
export default function MoodExplainer({ mood, open, onOpenChange }: MoodExplainerProps) {
  const band = moodBandByKey(mood.band)
  const score = Math.round(mood.score)
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Fear and greed · NEPSE"
      size="560"
      footer={
        <Link to="/market" className={buttonVariants({ variant: 'secondary', size: 'lg' })}>
          Open Market
        </Link>
      }
    >
      <div className="flex flex-col gap-6 pt-5">
        <div className="flex items-center gap-4">
          <img
            src={moodArt(mood).src}
            alt=""
            className="h-22 w-18 shrink-0 object-contain"
            width={72}
            height={88}
          />
          <div className="flex flex-col gap-1">
            <p className="text-body-xs text-muted-foreground-subtle">
              {isMoodToday(mood.date) ? 'Today' : `As of ${formatBsDate(mood.date)}`}
            </p>
            <p className="tnum text-h-2xl">
              {score}
              <span className="text-h-section text-muted-foreground"> · {band.name}</span>
            </p>
            <p className="text-body-m text-muted-foreground">{band.says}</p>
          </div>
        </div>

        <div aria-hidden="true" className="flex flex-col gap-2">
          <div className="@container relative flex h-3 gap-1">
            {MOOD_BANDS.map((each) => (
              <i
                key={each.key}
                className={cn(
                  'flex-1',
                  FILL[each.key],
                  each.key === band.key ? 'opacity-100' : 'opacity-45',
                )}
                style={{ flexGrow: each.to - each.from }}
              />
            ))}
            <Marker
              at={mood.score}
              className="absolute -top-1 h-5 w-1 -translate-x-1/2 bg-foreground"
              style={{ left: `${String(mood.score)}%` }}
            />
          </div>
          <div className="flex justify-between text-body-xs text-muted-foreground-subtle">
            <span>0 · weakest</span>
            <span>50 · typical day</span>
            <span>100 · strongest</span>
          </div>
        </div>

        <section className="flex flex-col gap-2">
          <h3 className="text-h-s">How it’s measured</h3>
          <p className="text-body-m text-muted-foreground">
            A blend of nine signals, among them momentum, breadth, new highs against new lows,
            turnover and circuit hits, each ranked against NEPSE’s own last 500 sessions (about two
            years). {score} means today reads stronger than about {score}% of those sessions, and 50
            is an ordinary day.
          </p>
        </section>

        <section className="flex flex-col gap-2">
          <h3 className="text-h-s">The five moods</h3>
          <ul className="-mx-3 flex flex-col">
            {MOOD_BANDS.map((each) => (
              <li
                key={each.key}
                aria-current={each.key === band.key ? 'true' : undefined}
                className={cn(
                  'flex items-baseline gap-3 px-3 py-2',
                  each.key === band.key ? 'bg-hover-wash' : null,
                )}
              >
                <i
                  aria-hidden="true"
                  className={cn('size-3 shrink-0 self-center', FILL[each.key])}
                />
                <span className="w-19 shrink-0 text-h-s">{each.name}</span>
                <span className="tnum w-11 shrink-0 text-body-xs text-muted-foreground-subtle">
                  {each.from}–{each.to}
                </span>
                <span className="text-body-m text-muted-foreground">{each.says}</span>
              </li>
            ))}
          </ul>
        </section>

        <p className="text-body-xs text-muted-foreground-subtle">
          It reads the mood of the market. It is not a signal to buy or sell.
        </p>
      </div>
    </Modal>
  )
}
